import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { PublicStorefront } from '../../services/storefrontService';
import { readCart, writeCart } from './storefrontHelpers';

interface StorefrontContextValue {
    slug: string;
    storefront: PublicStorefront;
    cart: number[];
    addToCart: (id: number) => void;
    removeFromCart: (id: number) => void;
    clearCart: () => void;
}

const StorefrontContext = createContext<StorefrontContextValue | null>(null);

export const StorefrontProvider: React.FC<{ slug: string; storefront: PublicStorefront; children: React.ReactNode }> = ({ slug, storefront, children }) => {
    const [cart, setCart] = useState<number[]>(() => readCart(slug));

    const update = useCallback(
        (fn: (current: number[]) => number[]) =>
            setCart((current) => {
                const next = fn(current);
                writeCart(slug, next);
                return next;
            }),
        [slug],
    );

    const value = useMemo<StorefrontContextValue>(
        () => ({
            slug,
            storefront,
            cart,
            addToCart: (id) => update((c) => (c.includes(id) ? c : [...c, id])),
            removeFromCart: (id) => update((c) => c.filter((x) => x !== id)),
            clearCart: () => update(() => []),
        }),
        [slug, storefront, cart, update],
    );

    return <StorefrontContext.Provider value={value}>{children}</StorefrontContext.Provider>;
};

export function useStorefront(): StorefrontContextValue {
    const ctx = useContext(StorefrontContext);
    if (!ctx) throw new Error('useStorefront muss innerhalb von StorefrontProvider verwendet werden');
    return ctx;
}
